/* ═══════════════════════════════════════════════════════════════
   TeamEditorPanel — the Directory tab's right-side panel for
   creating/editing a team. Replaces TeamsPanel.tsx (a Radix Dialog)
   per the Team Management UX Overhaul (2026-08-18): "side panels are
   THE pattern for People, modals survive only as destructive-action
   confirms." Same shell PersonPanel uses (fixed-width right dock).
   Team SELECTION now happens via the Directory team tree
   (TeamTree.tsx) instead of an in-dialog list — every field/action
   TeamsPanel had is preserved here: name*, description, cycle-safe
   parent picker, short code, color (shared ColorPickerPopover),
   delete w/ confirm (via the page's ConfirmDialog), member add/
   remove/leader-toggle.
   ═══════════════════════════════════════════════════════════════ */

import { Star, X, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { SearchableSelect } from '../../components/SearchableSelect';
import { ColorPickerPopover } from '../../components/ui/color-picker';
import type { TeamMember, Team, TeamAssignment } from '../../../shared/types';
import { TEAM_COLORS } from '../../lib/palette';
import {
  sectionHeading,
  fieldBlock,
  fieldLabel,
  inputStyle,
  emptyPill,
  displayName,
} from './styles';

export interface TeamEditorPanelProps {
  teams: Team[];
  members: TeamMember[];
  memberName: (id?: string) => string;
  canEdit: boolean;
  editingTeam: Team;
  teamDirty: boolean;
  editingTeamAssignments: TeamAssignment[];
  availableMembers: TeamMember[];
  addingMember: boolean;
  onUpdate: (updates: Partial<Team>) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  onAddMember: (memberId: string, role: 'leader' | 'member') => void;
  onRemoveMember: (assignmentId: string) => void;
  onChangeRole: (assignmentId: string, newRole: 'leader' | 'member') => void;
  onToggleAddMember: () => void;
  onClose: () => void;
}

export function TeamEditorPanel({
  teams,
  members,
  memberName,
  canEdit,
  editingTeam,
  teamDirty,
  editingTeamAssignments,
  availableMembers,
  addingMember,
  onUpdate,
  onSave,
  onDelete,
  onAddMember,
  onRemoveMember,
  onChangeRole,
  onToggleAddMember,
  onClose,
}: TeamEditorPanelProps) {
  // Teams available as parent (exclude self and descendants to prevent cycles)
  const getDescendantIds = (id: string): Set<string> => {
    const desc = new Set<string>();
    const queue = teams.filter((t) => t.parentTeamId === id);
    while (queue.length) {
      const t = queue.pop()!;
      desc.add(t.id);
      queue.push(...teams.filter((c) => c.parentTeamId === t.id));
    }
    return desc;
  };
  const descendantIds = editingTeam.id ? getDescendantIds(editingTeam.id) : new Set<string>();
  const parentOptions = teams.filter((t) => t.id !== editingTeam.id && !descendantIds.has(t.id));

  return (
    <div
      data-team-editor-panel
      style={{
        width: rem(440),
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--bd)',
        background: 'var(--bg)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          padding: 'var(--space-4) var(--space-4) var(--space-3)',
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span
            aria-hidden="true"
            style={{
              width: rem(10),
              height: rem(10),
              borderRadius: rem(3),
              background: editingTeam.color || 'var(--tm)',
              flexShrink: 0,
            }}
          />
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {editingTeam.id ? editingTeam.name || 'Team' : 'New Team'}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onClose}
          title="Close"
          aria-label="Close team panel"
        >
          <Icon icon={X} em={1.2308} />
        </Button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
        <div style={sectionHeading}>Team Details</div>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Team Name</span>
          <input
            value={editingTeam.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            style={inputStyle}
            placeholder="e.g. Mechanical Systems"
            autoFocus
            disabled={!canEdit}
          />
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Description</span>
          <textarea
            value={editingTeam.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            style={{ ...inputStyle, height: 'var(--space-8)', resize: 'vertical' }}
            placeholder="Optional description..."
            disabled={!canEdit}
          />
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Parent Team</span>
          <SearchableSelect
            value={editingTeam.parentTeamId || ''}
            options={parentOptions.map((t) => ({ value: t.id, label: t.name }))}
            onChange={(v) => onUpdate({ parentTeamId: v || undefined })}
            placeholder="-- None (top-level) --"
            searchPlaceholder="Search teams…"
            disabled={!canEdit}
          />
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Short Code (badge)</span>
          <input
            value={editingTeam.shortCode || ''}
            onChange={(e) => onUpdate({ shortCode: e.target.value })}
            style={{ ...inputStyle, width: rem(100) }}
            placeholder="e.g. M, E, P, T"
            maxLength={4}
            disabled={!canEdit}
          />
        </label>

        <div style={{ ...fieldBlock, marginBottom: rem(20) }}>
          <span style={fieldLabel}>Team Color</span>
          <ColorPickerPopover
            value={editingTeam.color || TEAM_COLORS[0]}
            onChange={(c) => onUpdate({ color: c })}
            allowAlpha={false}
            swatches={TEAM_COLORS}
            disabled={!canEdit}
            aria-label="Team color"
          />
        </div>

        {canEdit && (
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-5)' }}>
            <Button onClick={onSave}>{teamDirty ? 'Save •' : 'Save'}</Button>
            {editingTeam.id && (
              <Button
                variant="destructive"
                className="gap-1.5"
                onClick={() => onDelete(editingTeam.id)}
              >
                <Icon icon={Trash2} em={1.2727} aria-hidden="true" />
                Delete Team
              </Button>
            )}
          </div>
        )}

        {/* Members */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-2)',
          }}
        >
          <div style={sectionHeading}>Members</div>
          {editingTeam.id && canEdit && (
            <Button
              variant={addingMember ? 'secondary' : 'default'}
              size="sm"
              onClick={onToggleAddMember}
            >
              {addingMember ? 'Cancel' : '+ Add Member'}
            </Button>
          )}
        </div>

        {!editingTeam.id && <div style={emptyPill}>Save the team first to add members.</div>}

        {editingTeam.id && addingMember && canEdit && (
          <div style={{ marginBottom: rem(10) }}>
            <SearchableSelect
              value=""
              options={availableMembers.map((m) => ({
                value: m.id,
                label: displayName(m),
                hint: m.email || undefined,
              }))}
              onChange={(v) => v && onAddMember(v, 'member')}
              placeholder={
                availableMembers.length === 0
                  ? 'All members are already in this team'
                  : 'Search members to add…'
              }
              searchPlaceholder="Search members…"
              clearable={false}
              disabled={availableMembers.length === 0}
              autoOpenOnFocus
            />
          </div>
        )}

        {editingTeam.id && editingTeamAssignments.length === 0 && !addingMember && (
          <div style={emptyPill}>No members assigned yet.</div>
        )}

        {editingTeam.id && editingTeamAssignments.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {editingTeamAssignments.map((ta) => {
              const m = members.find((x) => x.id === ta.teamMemberId);
              const label = (m && displayName(m)) || memberName(ta.teamMemberId);
              return (
                <div
                  key={ta.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: `${rem(7)} ${rem(10)}`,
                    background: 'var(--sf)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--bd)',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 'var(--font-size-base)', fontWeight: 500 }}>
                    {label}
                  </span>
                  <div
                    role="group"
                    aria-label={`${label} team role`}
                    style={{
                      display: 'inline-flex',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--bd)',
                      overflow: 'hidden',
                      pointerEvents: canEdit ? 'auto' : 'none',
                      opacity: canEdit ? 1 : 0.6,
                    }}
                  >
                    {(['member', 'leader'] as const).map((opt) => {
                      const active = ta.teamRole === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => onChangeRole(ta.id, opt)}
                          aria-pressed={active}
                          title={`Set ${label} as ${opt}`}
                          style={{
                            padding: `${rem(2)} var(--space-2)`,
                            border: 'none',
                            background: active
                              ? 'color-mix(in srgb, var(--ac) 14%, transparent)'
                              : 'var(--sa)',
                            color: active ? 'var(--ac)' : 'var(--tm)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: active ? 700 : 600,
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                          }}
                        >
                          {opt === 'leader' && (
                            <Icon
                              icon={Star}
                              em={0.9091}
                              className="mr-1 inline"
                              fill={active ? 'currentColor' : 'none'}
                              aria-hidden="true"
                            />
                          )}
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-danger"
                      onClick={() => onRemoveMember(ta.id)}
                      title="Remove from team"
                      aria-label={`Remove ${label} from team`}
                    >
                      <Icon icon={X} em={1.0769} />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
